import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Table,
  Alert,
  Badge,
  Spinner,
} from "react-bootstrap";
import {
  FaSchool,
  FaPlus,
  FaTrash,
  FaGlobeAmericas,
} from "react-icons/fa";

const CreateSchoolsModal = ({
  show,
  onHide,
  getCountries,
  createSchoolsBatch,
  onSchoolsCreated,
}) => {
  const [rawSchoolText, setRawSchoolText] = useState("");
  const [parsedSchools, setParsedSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Parse options
  const [parseMode, setParseMode] = useState("newline");

  // Countries data
  const [countries, setCountries] = useState([]);
  const [defaultCountry, setDefaultCountry] = useState("");
  const [fetchingData, setFetchingData] = useState(false);

  // Fetch countries when modal opens
  useEffect(() => {
    if (show && getCountries) {
      fetchCountriesData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const fetchCountriesData = async () => {
    setFetchingData(true);
    try {
      const response = await getCountries();
      const countriesList = 
        response?.data && Array.isArray(response.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
      
      const sortedCountries = [...countriesList].sort((a, b) =>
        (a?.name || "").localeCompare(b?.name || "")
      );
      
      setCountries(sortedCountries);
      
      // Set first country as default if available
      if (sortedCountries.length > 0 && !defaultCountry) {
        setDefaultCountry(sortedCountries[0].id);
      }
    } catch (error) {
      console.error("Failed to fetch countries:", error);
      setError("Failed to load countries. Some features may not be available.");
    } finally {
      setFetchingData(false);
    }
  };

  // Parse school info whenever text or mode changes
  useEffect(() => {
    parseSchoolInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawSchoolText, parseMode, defaultCountry]);

  const parseSchoolInfo = () => {
    if (!rawSchoolText.trim()) {
      setParsedSchools([]);
      return;
    }

    let separator;
    switch (parseMode) {
      case "comma":
        separator = ",";
        break;
      case "tab":
        separator = "\t";
        break;
      case "newline":
      default:
        separator = "\n";
        break;
    }

    const schoolEntries = rawSchoolText
      .split(separator)
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0)
      .map((entry, index) => {
        // Try to parse: name, rollnumber, country, area, crmid
        // Expected formats:
        // "School Name | 12345 | Ireland"
        // "School Name - 12345"
        // "School Name, 12345, IE, Dublin, 999"
        
        let name = "";
        let rollnumber = "";
        let countryid = defaultCountry;
        let area = "";
        let crmid = "";

        // Try pipe separator first
        const pipeParts = entry.split("|").map(p => p.trim());
        if (pipeParts.length >= 2) {
          name = pipeParts[0];
          rollnumber = pipeParts[1];
          if (pipeParts.length >= 3) {
            // Try to match country by name
            const countryMatch = countries.find(c => 
              c.name.toLowerCase() === pipeParts[2].toLowerCase() ||
              c.id.toLowerCase() === pipeParts[2].toLowerCase()
            );
            if (countryMatch) {
              countryid = countryMatch.id;
            }
          }
          if (pipeParts.length >= 4) {
            area = pipeParts[3];
          }
          if (pipeParts.length >= 5) {
            crmid = pipeParts[4];
          }
        } else {
          // Try dash or comma separators
          const parts = entry.split(/[-,]/).map(p => p.trim());
          if (parts.length >= 2) {
            name = parts[0];
            rollnumber = parts[1];
            
            // Check for country in remaining parts
            if (parts.length >= 3) {
              const countryMatch = countries.find(c => 
                c.name.toLowerCase() === parts[2].toLowerCase() ||
                c.id.toLowerCase() === parts[2].toLowerCase()
              );
              if (countryMatch) {
                countryid = countryMatch.id;
              } else {
                // Might be area instead
                area = parts[2];
              }
            }
            if (parts.length >= 4) {
              area = parts[3];
            }
            if (parts.length >= 5) {
              crmid = parts[4];
            }
          } else {
            // Just a name, extract any numbers as potential roll number
            const numberMatch = entry.match(/\d+/);
            if (numberMatch) {
              rollnumber = numberMatch[0];
              name = entry.replace(numberMatch[0], "").trim();
            } else {
              name = entry;
            }
          }
        }

        // Validation
        const hasValidName = name.trim().length > 0;
        const hasValidRollNumber = rollnumber.trim().length > 0;
        const hasValidCountry = countryid && countryid !== "";
        const isValid = hasValidName && hasValidRollNumber && hasValidCountry;

        let validationError = "";
        if (!hasValidName && !hasValidRollNumber && !hasValidCountry) {
          validationError = "Name, roll number, and country are required";
        } else if (!hasValidName) {
          validationError = "School name is required";
        } else if (!hasValidRollNumber) {
          validationError = "Roll number is required";
        } else if (!hasValidCountry) {
          validationError = "Country is required";
        }

        return {
          id: `temp-${index}`,
          originalEntry: entry,
          name: name,
          rollnumber: rollnumber,
          countryid: countryid,
          area: area,
          crmid: crmid,
          isValid: isValid,
          validationError: validationError,
        };
      });

    setParsedSchools(schoolEntries);
  };

  const updateSchoolField = (schoolId, field, value) => {
    setParsedSchools((prev) =>
      prev.map((school) => {
        if (school.id === schoolId) {
          const updatedSchool = { ...school, [field]: value };

          // Re-validate when required fields change
          if (field === "name" || field === "rollnumber" || field === "countryid") {
            const nameValue = field === "name" ? value : school.name;
            const rollnumberValue = field === "rollnumber" ? value : school.rollnumber;
            const countryidValue = field === "countryid" ? value : school.countryid;

            const hasValidName = nameValue.trim().length > 0;
            const hasValidRollNumber = rollnumberValue.trim().length > 0;
            const hasValidCountry = countryidValue && countryidValue !== "";
            const isValid = hasValidName && hasValidRollNumber && hasValidCountry;

            let validationError = "";
            if (!hasValidName && !hasValidRollNumber && !hasValidCountry) {
              validationError = "Name, roll number, and country are required";
            } else if (!hasValidName) {
              validationError = "School name is required";
            } else if (!hasValidRollNumber) {
              validationError = "Roll number is required";
            } else if (!hasValidCountry) {
              validationError = "Country is required";
            }

            updatedSchool.isValid = isValid;
            updatedSchool.validationError = validationError;
          }

          return updatedSchool;
        }
        return school;
      })
    );
  };

  const removeSchool = (schoolId) => {
    setParsedSchools((prev) =>
      prev.filter((school) => school.id !== schoolId)
    );
  };

  const addManualSchool = () => {
    const newSchool = {
      id: `manual-${Date.now()}`,
      originalEntry: "",
      name: "",
      rollnumber: "",
      countryid: defaultCountry,
      area: "",
      crmid: "",
      isValid: false,
      validationError: "Name, roll number, and country are required",
    };
    setParsedSchools((prev) => [...prev, newSchool]);
  };

  const handleCreateSchools = async () => {
    const validSchools = parsedSchools.filter((school) => school.isValid);
    if (validSchools.length === 0) {
      setError("Please add at least one valid school with name, roll number, and country.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Prepare schools data for API
      const schoolsToCreate = validSchools.map((school) => {
        const schoolData = {
          name: school.name.trim(),
          countryid: school.countryid,
          rollnumber: school.rollnumber.trim(),
        };

        if (school.area && school.area.trim() !== "") {
          schoolData.area = school.area.trim();
        }

        if (school.crmid && school.crmid.toString().trim() !== "") {
          schoolData.crmid = parseInt(school.crmid, 10);
        }

        return schoolData;
      });

      // Call the batch create function
      await createSchoolsBatch(schoolsToCreate);

      // Success
      setSuccess(`Successfully created ${validSchools.length} school(s)!`);

      // Call callback if provided
      if (onSchoolsCreated) {
        onSchoolsCreated(validSchools);
      }

      // Reset form
      setRawSchoolText("");
      setParsedSchools([]);

      // Close modal after delay
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      setError("Failed to create schools. Please try again.");
      console.error("Create schools error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setRawSchoolText("");
    setParsedSchools([]);
    setError("");
    setSuccess("");
    setParseMode("newline");
    setDefaultCountry("");
    onHide();
  };

  const validSchoolsCount = parsedSchools.filter((s) => s.isValid).length;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      dialogClassName="custom-modal-width"
    >
      <style>
        {`
          .custom-modal-width {
            max-width: 95% !important;
          }
          .custom-modal-width .modal-dialog {
            max-width: 95% !important;
          }
        `}
      </style>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaSchool className="me-2" />
          Create Schools (Batch)
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ minHeight: "700px" }}>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {fetchingData && (
          <Alert variant="info">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading countries...
          </Alert>
        )}

        <Row className="h-100">
          {/* Column 1: Input Options & Settings */}
          <Col md={4} className="border-end">
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Add Schools</h5>
                <Button variant="outline-primary" size="sm" onClick={addManualSchool}>
                  <FaPlus className="me-1" />
                  Add One
                </Button>
              </div>

              {/* Global Settings */}
              <div className="mb-4 p-3 bg-light rounded">
                <h6 className="text-primary mb-3">
                  <FaGlobeAmericas className="me-1" />
                  Default Settings
                </h6>

                {/* Default Country Selection */}
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">
                    Default Country for New Schools
                  </Form.Label>
                  <Form.Select
                    value={defaultCountry}
                    onChange={(e) => {
                      const newDefault = e.target.value;
                      setDefaultCountry(newDefault);
                      // Update existing schools that still match the previous default country
                      setParsedSchools((prev) =>
                        prev.map((s) =>
                          s.countryid === defaultCountry 
                            ? { ...s, countryid: newDefault } 
                            : s
                        )
                      );
                    }}
                    size="sm"
                  >
                    <option value="">Select country...</option>
                    {countries.map((country) => (
                      <option key={country.id} value={country.id}>
                        {country.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted small">
                    This will be used for bulk imports if country is not specified
                  </Form.Text>
                </Form.Group>
              </div>

              {/* Bulk Import Section */}
              <div className="mb-4 p-3 bg-light rounded">
                <h6 className="text-primary mb-2">💡 Bulk Import - Save Time!</h6>
                <p className="small text-muted mb-2">
                  Adding multiple schools? Copy and paste them all at once.
                </p>

                {/* Parse Mode Selection */}
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">Format</Form.Label>
                  <Form.Select
                    value={parseMode}
                    onChange={(e) => setParseMode(e.target.value)}
                    size="sm"
                  >
                    <option value="newline">One school per line</option>
                    <option value="comma">Comma separated</option>
                    <option value="tab">Tab separated</option>
                  </Form.Select>
                </Form.Group>

                {/* Text Area */}
                <Form.Group className="mb-2">
                  <Form.Label className="small fw-bold">
                    Paste School List{" "}
                    <Badge bg="secondary" className="ms-1">
                      Name, Roll # &amp; Country Required
                    </Badge>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={8}
                    placeholder={`Example formats - try copying and pasting:

St. Mary's Primary | 12345 | Ireland
Greendale Secondary - 67890 - UK
Riverside Academy, 11223, Ireland, Dublin, 500
Oakwood School | 99887

Tip: Use | (pipe), - (dash), or , (comma) to separate fields
Order: Name | Roll Number | Country | Area | CRM ID`}
                    value={rawSchoolText}
                    onChange={(e) => setRawSchoolText(e.target.value)}
                    className="font-monospace"
                    style={{ fontSize: "0.85rem" }}
                  />
                </Form.Group>
                <Form.Text className="text-muted small">
                  ✅ Automatically detects school names, roll numbers, and countries
                </Form.Text>
              </div>

              {/* Status */}
              {parsedSchools.length > 0 && (
                <div className="text-center">
                  <Badge
                    bg={validSchoolsCount > 0 ? "success" : "warning"}
                    className="p-2"
                  >
                    📊 {parsedSchools.length} found • {validSchoolsCount} valid
                  </Badge>
                </div>
              )}
            </div>
          </Col>

          {/* Column 2: Schools Table */}
          <Col md={8}>
            <div className="mb-3">
              <h5 className="mb-3">
                Schools to Create
                <Badge bg="primary" className="ms-2">
                  {validSchoolsCount}
                </Badge>
              </h5>

              <div style={{ height: "550px", overflowY: "auto" }}>
                {parsedSchools.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <FaSchool size={48} className="mb-3 opacity-50" />
                    <h6>No Schools Added Yet</h6>
                    <p className="mb-2">Get started by:</p>
                    <div className="text-start d-inline-block">
                      <p className="mb-1">
                        • Clicking <strong>"Add One"</strong> for single entry
                      </p>
                      <p className="mb-1">
                        • Using <strong>"Bulk Import"</strong> to paste multiple
                      </p>
                    </div>
                    <small className="text-muted">
                      Name, roll number, and country are required for each school
                    </small>
                  </div>
                ) : (
                  <Table striped bordered hover size="sm">
                    <thead className="sticky-top bg-light">
                      <tr>
                        <th style={{ width: "250px" }}>School Name *</th>
                        <th style={{ width: "150px" }}>Roll Number *</th>
                        <th style={{ width: "180px" }}>Country *</th>
                        <th style={{ width: "150px" }}>Area</th>
                        <th style={{ width: "100px" }}>CRM ID</th>
                        <th style={{ width: "60px" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedSchools.map((school) => (
                        <tr
                          key={school.id}
                          className={!school.isValid ? "table-warning" : ""}
                        >
                          <td>
                            <Form.Control
                              size="sm"
                              placeholder="School name *"
                              value={school.name}
                              onChange={(e) =>
                                updateSchoolField(
                                  school.id,
                                  "name",
                                  e.target.value
                                )
                              }
                              isInvalid={
                                !school.isValid && !school.name.trim()
                              }
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              placeholder="Roll # *"
                              value={school.rollnumber}
                              onChange={(e) =>
                                updateSchoolField(
                                  school.id,
                                  "rollnumber",
                                  e.target.value
                                )
                              }
                              isInvalid={
                                !school.isValid && !school.rollnumber.trim()
                              }
                            />
                          </td>
                          <td>
                            <Form.Select
                              size="sm"
                              value={school.countryid}
                              onChange={(e) =>
                                updateSchoolField(
                                  school.id,
                                  "countryid",
                                  e.target.value
                                )
                              }
                              isInvalid={
                                !school.isValid && !school.countryid
                              }
                            >
                              <option value="">Select country...</option>
                              {countries.map((country) => (
                                <option key={country.id} value={country.id}>
                                  {country.name}
                                </option>
                              ))}
                            </Form.Select>
                            {!school.isValid && school.validationError && (
                              <small className="text-danger d-block mt-1">
                                {school.validationError}
                              </small>
                            )}
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              placeholder="Area (optional)"
                              value={school.area}
                              onChange={(e) =>
                                updateSchoolField(
                                  school.id,
                                  "area",
                                  e.target.value
                                )
                              }
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              type="number"
                              placeholder="CRM ID"
                              value={school.crmid}
                              onChange={(e) =>
                                updateSchoolField(
                                  school.id,
                                  "crmid",
                                  e.target.value
                                )
                              }
                            />
                          </td>
                          <td className="text-center">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeSchool(school.id)}
                            >
                              <FaTrash />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100 align-items-center">
          <div className="text-muted">
            {validSchoolsCount > 0 && (
              <span>
                <FaSchool className="me-1" />
                Creating {validSchoolsCount} school(s)
              </span>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={handleClose} className="me-2">
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateSchools}
              disabled={validSchoolsCount === 0 || loading}
            >
              {loading
                ? "Creating Schools..."
                : `Create ${validSchoolsCount} School(s)`}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateSchoolsModal;